import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Guide } from '@/constants/guides'

import { ArrowLeft } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FadeLink } from '@/components/generals/fade-link'
import { Reveal } from '@/components/generals/reveal'
import { RingsDivider } from '@/components/generals/hairline'
import { DIAGRAMS } from '@/components/learn/diagrams'
import { GUIDES, guideBySlug } from '@/constants/guides'

type Props = {
    guide: Guide
    next: Pick<Guide, 'slug' | 'title' | 'summary'>
}

const GuidePage: FC<Props> = ({ guide, next }): ReactNode => {
    const Diagram = DIAGRAMS[guide.diagram]

    return (
        <Layout>
            <Seo title={guide.title} description={guide.summary} />

            <article className='pb-24 pt-28 md:pt-36'>
                <header className='container-page'>
                    <FadeLink href='/learn' className='label-mono inline-flex items-center gap-2 text-star-3 transition-colors hover:text-star'>
                        <ArrowLeft aria-hidden className='size-3.5' strokeWidth={1.25} />
                        All guides
                    </FadeLink>
                    <p className='label-mono mt-10 text-brass'>
                        {`${guide.level} · ${guide.minutes} minute read`}
                    </p>
                    <h1 className='display-serif mt-5 max-w-4xl text-5xl text-star md:text-7xl'>
                        {guide.title}
                    </h1>
                    <p className='mt-6 max-w-2xl font-heading text-2xl font-light leading-snug text-star-2'>
                        {guide.summary}
                    </p>
                </header>

                <Reveal className='container-page mt-14'>
                    <figure className='border border-hair bg-ink-2 p-6 md:p-12'>
                        <div className='mx-auto aspect-[4/3] max-w-3xl'>
                            <Diagram />
                        </div>
                        <figcaption className='label-mono mt-6 text-center text-star-3'>
                            {guide.caption}
                        </figcaption>
                    </figure>
                </Reveal>

                <div className='container-page mt-16'>
                    <div className='prose-observatory mx-auto max-w-2xl text-[1.0625rem] leading-[1.8]'>
                        {guide.sections.map(section => (
                            <section key={section.heading}>
                                <h3>
                                    {section.heading}
                                </h3>
                                {section.paragraphs.map(paragraph => (
                                    <p key={paragraph}>
                                        {paragraph}
                                    </p>
                                ))}
                                {section.steps && (
                                    <ol>
                                        {section.steps.map(step => (
                                            <li key={step}>
                                                {step}
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </section>
                        ))}
                    </div>
                </div>

                <div className='container-page mt-24'>
                    <RingsDivider />
                    <FadeLink href={`/learn/${next.slug}`} className='group mx-auto mt-14 block max-w-2xl text-center'>
                        <span className='label-mono text-star-3'>
                            Next guide
                        </span>
                        <span className='mt-3 block font-heading text-4xl text-star transition-colors duration-500 group-hover:text-brass'>
                            {next.title}
                        </span>
                        <span className='mt-3 block text-star-2'>
                            {next.summary}
                        </span>
                    </FadeLink>
                </div>
            </article>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: GUIDES.map(guide => ({ params: { slug: guide.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
    const guide = guideBySlug(String(params?.slug ?? ''))

    if (!guide) return { notFound: true }

    const index = GUIDES.findIndex(item => item.slug === guide.slug)
    const following = GUIDES[(index + 1) % GUIDES.length]

    return {
        props: {
            guide,
            next: { slug: following.slug, title: following.title, summary: following.summary }
        }
    }
}

export default GuidePage
